import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import ProfileAvatarStack, {
  ProfileAvatarStackSkeleton,
} from "@/user/common/avatar-stack/ProfileAvatarStack"
import ProfileListDrawer from "@/user/common/profile-list-drawer/ProfileListDrawer"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoText, DiscoTextSkeleton } from "@disco-ui"
import { useState } from "react"
import { useLazyLoadQuery } from "react-relay"
import { graphql } from "relay-runtime"
import { OccurrenceHostsLabelQuery } from "./__generated__/OccurrenceHostsLabelQuery.graphql"

interface Props {
  occurrenceId: GlobalID
  viewOnly?: boolean
  variant?: "avatar" | "text"
}

function OccurrenceHostsLabel(props: Props) {
  const { occurrenceId, viewOnly, variant = "avatar" } = props
  const [isModalOpen, setIsModalOpen] = useState(false)
  const classes = useStyles()

  const { occurrence } = useLazyLoadQuery<OccurrenceHostsLabelQuery>(
    graphql`
      query OccurrenceHostsLabelQuery($id: ID!) {
        occurrence: node(id: $id) {
          ... on Occurrence {
            hosts {
              totalCount
              edges {
                node {
                  id
                  user {
                    fullName
                    ...ProfileListDrawerListItemFragment
                    ...ProfileAvatarStackFragment @relay(mask: false)
                  }
                }
              }
            }
          }
        }
      }
    `,
    { id: occurrenceId }
  )

  const hosts = Relay.connectionToArray(occurrence?.hosts)
  const hostUsers = hosts.map((host) => host.user)
  const hostCount = occurrence?.hosts?.totalCount

  if (!hosts.length) return null

  return (
    <>
      {variant === "text" ? (
        <div className={classes.cardHostLabel}>
          <ProfileAvatarStack
            testid={"OccurrenceHostsAvatarStack"}
            users={hostUsers}
            totalUsers={Number(hostCount)}
            onClick={handleOnClick}
            countVariant={"never"}
            avatarSize={24}
            className={classes.avatars}
            disableHover={viewOnly}
            hideBorder
            unstyledButton
          />
          <DiscoText
            truncateText={1}
            variant={"body-sm"}
            color={"text.secondary"}
            noWrap
            marginLeft={1}
          >
            <DiscoText variant={"body-sm"} color={"text.secondary"} component={"span"}>
              {"Hosted by "}
            </DiscoText>
            {hosts.reduce(
              (s, host) =>
                s.length ? `${s}, ${host.user.fullName}` : host.user.fullName,
              ""
            )}
          </DiscoText>
        </div>
      ) : (
        <ProfileAvatarStack
          testid={"OccurrenceHostsAvatarStack"}
          users={hostUsers}
          totalUsers={Number(hostCount)}
          onClick={handleOnClick}
          avatarSize={24}
          className={classes.avatars}
          customUsersLabel={{ plural: "Hosts", singular: "Host" }}
          singleUserCopy={hosts[0].user.fullName}
          disableHover={viewOnly}
        />
      )}

      <ProfileListDrawer
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        users={hostUsers}
        title={"Hosts"}
      />
    </>
  )

  function handleOnClick() {
    if (viewOnly) return
    setIsModalOpen(true)
  }
}

const useStyles = makeUseStyles({
  avatars: {
    alignItems: "flex-end",
    height: "100%",
  },
  cardHostLabel: {
    display: "flex",
    alignItems: "center",
    width: "inherit",
  },
})

export function OccurrenceHostsLabelSkeleton() {
  return (
    <>
      <ProfileAvatarStackSkeleton />
      <DiscoTextSkeleton width={40} />
    </>
  )
}

export default Relay.withSkeleton({
  component: OccurrenceHostsLabel,
  skeleton: OccurrenceHostsLabelSkeleton,
})
